import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { isLimitError } from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { storage } from '../product/productStorage';
import { formatLocalDate, getWeekStart, kindForContentType } from '../product/productUtils';
import { chooseSlot, formatDayOfWeek, formatShortDay, weekOfDate } from '../utils/planInsights';
import './ideas.css';

// The Content plan's "Ideas" panel: topic ideas from what worked, upcoming dates
// and the content pillars (POST /ideas, kept on the server so reopening costs
// nothing). Each idea can be added to the plan (no AI) or created right away.

const SOURCES = [
    { id: 'results', label: 'From your results', icon: 'trending_up' },
    { id: 'calendar', label: 'Coming up', icon: 'event' },
    { id: 'pillar', label: 'Your pillars', icon: 'view_column' },
];

const toText = (value) => (typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '');
const isDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime());

// The server's list in a shape that's safe to render
export const normalizeIdeas = (raw) => {
    const list = Array.isArray(raw?.ideas) ? raw.ideas : [];
    const seen = new Set();
    return list
        .filter(item => item && typeof item === 'object' && !Array.isArray(item))
        .map((item, index) => ({
            id: toText(item.id) || `idea-${index}`,
            title: toText(item.title),
            angle: toText(item.angle),
            hook: toText(item.hook),
            why: toText(item.why),
            source: SOURCES.some(source => source.id === item.source) ? item.source : 'pillar',
            platform: toText(item.platform),
            contentType: toText(item.contentType),
            date: isDate(toText(item.date)) ? toText(item.date) : '',
            moment: toText(item.moment),
        }))
        .filter(idea => idea.title && !seen.has(idea.id) && seen.add(idea.id));
};

// "Best times" from tracked link clicks, or how many clicks it takes to get them
export const BestTimesHint = ({ bestTimes, className = '' }) => {
    if (!bestTimes) return null;
    if (!bestTimes.enough) {
        return (
            <p className={`ls-best ${className}`}>
                <Icon name="schedule" size={16} />
                <span>
                    Best times to post show up after {bestTimes.minimum} tracked link clicks
                    {bestTimes.clicks ? ` (${bestTimes.clicks} so far)` : ''}.
                </span>
            </p>
        );
    }
    const groups = bestTimes.platforms.length ? bestTimes.platforms : [{ platform: 'All platforms', slots: bestTimes.overall }];
    return (
        <p className={`ls-best ${className}`}>
            <Icon name="schedule" size={16} />
            <span>
                <b>Best times</b>
                {groups.map(group => (
                    <span key={group.platform} className="ls-best-group">
                        {group.platform}: {group.slots.map(slot => slot.label).join(' · ')}
                    </span>
                ))}
            </span>
        </p>
    );
};

const IdeasPanel = ({ weekStart, isPastWeek, posts, bestTimes, onAddPost, onOpenWeek }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(() => storage.get('planIdeasOpen') === 'true');
    const [ideas, setIdeas] = useState([]);
    const [meta, setMeta] = useState({}); // { createdAt }
    const [status, setStatus] = useState('loading'); // loading | idle | generating | load-error
    const [error, setError] = useState('');
    const [adding, setAdding] = useState({}); // { ideaId: true }
    const [added, setAdded] = useState({}); // { ideaId: { weekStart, day } }
    const [itemErrors, setItemErrors] = useState({}); // { ideaId: message }
    const loaded = useRef(false);

    const load = useCallback(async () => {
        setStatus('loading');
        try {
            const data = await api.getIdeas();
            setIdeas(normalizeIdeas(data));
            setMeta({ createdAt: toText(data?.createdAt) });
            setStatus('idle');
        } catch (e) {
            setStatus('load-error');
        }
    }, []);

    // The kept list loads the first time the panel opens
    useEffect(() => {
        if (open && !loaded.current) {
            loaded.current = true;
            load();
        }
    }, [open, load]);

    const toggle = () => {
        setOpen(!open);
        storage.set('planIdeasOpen', String(!open));
    };

    const generate = async () => {
        setStatus('generating');
        setError('');
        try {
            // Ideas for the week on screen, or this week when looking back
            const data = await api.generateIdeas(isPastWeek ? getWeekStart() : weekStart);
            const list = normalizeIdeas(data);
            if (!list.length) throw new Error('No ideas');
            setIdeas(list);
            setMeta({ createdAt: toText(data?.createdAt) });
            setAdded({});
            setItemErrors({});
        } catch (e) {
            if (!isLimitError(e)) {
                setError(e.response?.status === 400 && toText(e.response?.data?.error)
                    ? toText(e.response.data.error)
                    : "Couldn't get ideas this time. Nothing was used up, so please try again.");
            }
        } finally {
            setStatus('idle');
        }
    };

    // Calendar ideas go on their date (in that date's week); the rest go in the week on screen
    const placeFor = (idea) => {
        const today = formatLocalDate(new Date());
        if (idea.date && idea.date >= today) {
            const target = weekOfDate(idea.date);
            return { weekStart: target.weekStart, preferredDay: target.day, posts: target.weekStart === weekStart ? posts : null };
        }
        return { weekStart: isPastWeek ? getWeekStart() : weekStart, posts: isPastWeek ? null : posts };
    };

    const addIdea = async (idea) => {
        const place = placeFor(idea);
        setAdding(previous => ({ ...previous, [idea.id]: true }));
        setItemErrors(previous => ({ ...previous, [idea.id]: '' }));
        try {
            // Another week's posts come from the server, so the new post finds a free day there
            const weekPosts = place.posts || (await api.getPlan(place.weekStart))?.posts || [];
            const slot = chooseSlot({
                weekStart: place.weekStart,
                posts: weekPosts,
                platform: idea.platform,
                bestTimes,
                preferredDay: place.preferredDay,
            });
            if (!slot) throw Object.assign(new Error('No day'), { friendly: 'This week has no days left. Add it from next week.' });
            await onAddPost(place.weekStart, {
                title: idea.title,
                angle: idea.angle,
                hook: idea.hook,
                why: idea.why || (idea.moment ? `Ties in with ${idea.moment}` : ''),
                platform: idea.platform,
                contentType: idea.contentType,
                day: slot.day,
                time: slot.time,
                origin: 'idea',
            });
            setAdded(previous => ({ ...previous, [idea.id]: { weekStart: place.weekStart, day: slot.day } }));
        } catch (e) {
            setItemErrors(previous => ({
                ...previous,
                [idea.id]: e.friendly || toText(e.response?.data?.error) || "Couldn't add it to the plan. Please try again.",
            }));
        } finally {
            setAdding(previous => ({ ...previous, [idea.id]: false }));
        }
    };

    const createNow = (idea) => {
        navigate(`/dashboard/${kindForContentType(idea.contentType)}`, {
            state: {
                seed: {
                    topic: [idea.title, idea.angle].filter(Boolean).join(': '),
                    hook: idea.hook,
                    platform: idea.platform,
                    from: 'From your ideas',
                },
            },
        });
    };

    const groups = useMemo(() => SOURCES
        .map(source => ({ ...source, ideas: ideas.filter(idea => idea.source === source.id) }))
        .filter(group => group.ideas.length), [ideas]);

    const isGenerating = status === 'generating';
    const isLoading = status === 'loading';
    const countLabel = ideas.length ? `${ideas.length} ideas` : 'New';
    const created = meta.createdAt ? new Date(meta.createdAt) : null;
    const createdLabel = created && !Number.isNaN(created.getTime()) ? created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

    const renderIdea = (idea, index) => {
        const done = added[idea.id];
        const busy = Boolean(adding[idea.id]);
        const chips = [idea.platform, idea.contentType].filter(Boolean);
        return (
            <article key={idea.id} className={`ls-ideas-item${done ? ' is-added' : ''}`} style={{ '--i': index }}>
                {(idea.date || chips.length > 0) && (
                    <div className="ls-ideas-item-meta">
                        {idea.date && (
                            <span className="ls-ideas-date">
                                <Icon name="event" size={14} />
                                {formatShortDay(idea.date)}{idea.moment ? ` · ${idea.moment}` : ''}
                            </span>
                        )}
                        {chips.map(chip => <span key={chip} className="ls-chip">{chip}</span>)}
                    </div>
                )}
                <h4 className="ls-ideas-item-title">{idea.title}</h4>
                {idea.angle && <p className="ls-ideas-item-angle">{idea.angle}</p>}
                {idea.why && <p className="ls-ideas-item-why">{idea.why}</p>}
                <div className="ls-ideas-item-actions">
                    {done ? (
                        <span className="ls-ideas-added" role="status">
                            <Icon name="check_circle" size={16} />
                            Added · {formatDayOfWeek(done.weekStart, done.day)}
                            {done.weekStart !== weekStart && (
                                <button type="button" className="ls-link" onClick={() => onOpenWeek(done.weekStart)}>View week</button>
                            )}
                        </span>
                    ) : (
                        <button type="button" className="ls-btn ls-btn-secondary ls-ideas-btn" onClick={() => addIdea(idea)} disabled={busy}>
                            {busy ? <span className="ls-spinner" /> : <Icon name="add" size={16} />}Add to plan
                        </button>
                    )}
                    <button type="button" className="ls-btn ls-btn-ghost ls-ideas-btn" onClick={() => createNow(idea)}>
                        <Icon name="edit_note" size={16} />Create now
                    </button>
                </div>
                {itemErrors[idea.id] && (
                    <Alert severity="error" onClose={() => setItemErrors(previous => ({ ...previous, [idea.id]: '' }))}>{itemErrors[idea.id]}</Alert>
                )}
            </article>
        );
    };

    const renderBody = () => {
        if (status === 'load-error') {
            return (
                <Alert severity="error" action={<button type="button" className="ls-btn ls-btn-secondary ls-ideas-retry" onClick={load}>Try again</button>}>
                    Couldn't load your ideas. Check your connection, then try again.
                </Alert>
            );
        }
        if (isLoading || isGenerating) {
            return (
                <div className="ls-ideas-groups" aria-busy="true">
                    <div className="ls-ideas-group">
                        <span className="ls-skeleton-text" style={{ width: '9em' }} />
                        <div className="ls-ideas-list">
                            {[0, 1, 2].map(index => <div key={index} className="ls-skeleton ls-ideas-skeleton" />)}
                        </div>
                    </div>
                </div>
            );
        }
        if (!ideas.length) {
            return (
                <div className="ls-placeholder ls-ideas-placeholder">
                    <Icon name="lightbulb" size={28} />
                    <span>Get 8–12 topic ideas from what worked, dates coming up in the next 6 weeks and your content pillars.</span>
                </div>
            );
        }
        let index = 0;
        return (
            <div className="ls-ideas-groups">
                {groups.map(group => (
                    <section key={group.id} className="ls-ideas-group" aria-labelledby={`ideas-${group.id}`}>
                        <h3 className="ls-ideas-group-title" id={`ideas-${group.id}`}>
                            <Icon name={group.icon} size={16} />{group.label}
                            <span className="ls-ideas-group-count">{group.ideas.length}</span>
                        </h3>
                        <div className="ls-ideas-list ls-stagger">
                            {group.ideas.map(idea => renderIdea(idea, index++))}
                        </div>
                    </section>
                ))}
            </div>
        );
    };

    return (
        <section className={`ls-card ls-plan-auto ls-ideas${open ? ' is-open' : ''}`} aria-labelledby="plan-ideas-title">
            <div className="ls-plan-auto-head">
                <h2 className="ls-card-title ls-plan-heading" id="plan-ideas-title">
                    <button type="button" className="ls-plan-auto-toggle" onClick={toggle} aria-expanded={open} aria-controls="plan-ideas-body">
                        <Icon name="lightbulb" size={18} className="ls-plan-auto-icon" />
                        Ideas
                        <span className={`ls-plan-auto-count${ideas.length ? ' is-on' : ''}`}>{countLabel}</span>
                        <span className="ls-plan-auto-summary">Topics from your results, upcoming dates and pillars</span>
                        <Icon name="expand_more" size={20} className="ls-plan-auto-chevron" />
                    </button>
                </h2>
            </div>
            {open && (
                <div className="ls-plan-auto-body ls-ideas-body" id="plan-ideas-body">
                    <div className="ls-ideas-bar">
                        <BestTimesHint bestTimes={bestTimes} />
                        <button
                            type="button"
                            className="ls-btn ls-btn-secondary ls-ideas-get"
                            onClick={generate}
                            disabled={isGenerating || isLoading}
                        >
                            {isGenerating ? <span className="ls-spinner" /> : <Icon name={ideas.length ? 'refresh' : 'auto_awesome'} size={18} />}
                            {ideas.length ? 'Refresh ideas' : 'Get ideas'}
                        </button>
                    </div>
                    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                    {isPastWeek && ideas.length > 0 && (
                        <Alert severity="info">This week is over, so ideas you add go to this week or the week of their date.</Alert>
                    )}
                    {renderBody()}
                    <p className="ls-plan-auto-note">
                        Getting ideas uses a generation{createdLabel && ideas.length ? ` · these are from ${createdLabel}` : ''}.
                        Adding one to the plan doesn't.
                    </p>
                </div>
            )}
        </section>
    );
};

export default IdeasPanel;
